import React from 'react';
import { HAT_ASPECT, BAND, ART_TILT_DEG } from './hatGeometry';

const HAT_SRC = `${process.env.PUBLIC_URL || ''}/jesterhat.svg`;

// =============================================================================
// JesterHat — the "noodle shame" overlay (the silly bit).
//
// When a member starts a sesh that turns out to be a NOODLE (a near-miss that
// landed just shy of the :20/:50 instead of on it), they wear this jester hat on
// their avatar until the 420/350 they fumbled actually arrives. The server owns
// the timing (seshService noodle-shame registry → `sesh:noodle-shame`); this is
// purely the visual.
//
// It's an absolutely-positioned overlay meant to sit INSIDE a `position:
// relative` avatar wrapper. It is INTENTIONALLY allowed to overflow the avatar's
// box — the hat perches above and spills past the edges (per the brief: "overlaid
// on top even if it overflows out"), so the wrapper must NOT clip
// (overflow:visible) for the full perch to show. It renders above the avatar
// (high z-index) and never intercepts clicks (pointer-events:none) so the
// underlying avatar / user-row interactions keep working.
//
// Props:
//   size       Avatar diameter — a number (px) OR any CSS length string (e.g.
//              '2.286em' to track an em-sized avatar). Default 40. The hat width
//              is ~1.25× this so the floppy points spill past the avatar edges.
//   title      Tooltip text (e.g. "Bob noodled it 🍜"). Optional.
//   style      Extra styles merged onto the <img> (escape hatch for nudging).
//   className  Forwarded to the <img>.
// =============================================================================

// Art's native aspect ratio (w/h). Used to derive height from width so the hat
// never squashes regardless of how `size` is expressed.
const ASPECT = 565.59442 / 448.49576; // ≈ 1.261
const WIDTH_FACTOR = 1.25; // hat is a touch wider than the avatar

const JesterHat = ({ size = 40, title, style, className }) => {
  // Accept px numbers or CSS strings. For a string we scale with calc() so an
  // em-sized avatar stays proportional; for a number we just multiply.
  const hatW =
    typeof size === 'number'
      ? size * WIDTH_FACTOR
      : `calc(${size} * ${WIDTH_FACTOR})`;
  const hatH =
    typeof size === 'number'
      ? hatW / ASPECT
      : `calc(${size} * ${WIDTH_FACTOR} / ${ASPECT})`;

  return (
    <img
      src={HAT_SRC}
      alt=""
      aria-hidden="true"
      title={title}
      className={className}
      style={{
        position: 'absolute',
        // Center horizontally over the avatar; pull up so the hat sits ON the
        // head with its lower bells dipping onto the avatar's crown. The negative
        // translate is what makes it overflow out the top of the avatar box.
        left: '50%',
        top: 0,
        width: hatW,
        height: hatH,
        transform: 'translate(-50%, -68%)',
        transformOrigin: 'bottom center',
        pointerEvents: 'none',
        zIndex: 5,
        // A soft drop shadow lifts it off whatever avatar / background is behind.
        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.55))',
        userSelect: 'none',
        ...style,
      }}
      draggable={false}
    />
  );
};

// =============================================================================
// TrackedJesterHat — the head-FOLLOWING variant for a video tile.
//
// Given a normalized transform from headTracker ({ cx, cy, wFrac, rollDeg } — all
// fractions of the VIDEO FRAME + degrees) and the tile's rendered pixel size, it
// scales the SVG so the BAND spans wFrac of the tile, anchors the band center at
// (cx, cy), and rotates by (head roll + ART_TILT correction) about that point.
// Absolutely positioned to fill its (position:relative) parent — the video frame.
//
// Props:
//   transform  { cx, cy, wFrac, rollDeg } or null (null → render nothing).
//   tileW, tileH  the video frame's rendered pixel dimensions.
//   title      tooltip text.
// =============================================================================
export const TrackedJesterHat = ({ transform, tileW, tileH, title }) => {
  if (!transform || !tileW || !tileH) return null;
  const { cx, cy, wFrac, rollDeg } = transform;
  // Band should span wFrac of the tile width → blow up to full SVG width by the
  // band's own width fraction within the art.
  const hatW = (wFrac * tileW) / BAND.wFrac;
  const hatH = hatW / HAT_ASPECT;
  // Anchor the band CENTER (cxFrac/cyFrac of the box) at the head point (cx,cy),
  // in tile pixels. Rotate about that same point (transform-origin), applying the
  // art-tilt correction so the band is level at head-roll 0, then the head roll.
  const px = cx * tileW, py = cy * tileH;
  const rot = ART_TILT_DEG + (rollDeg || 0);
  return (
    <img
      src={HAT_SRC}
      alt=""
      aria-hidden="true"
      title={title}
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: hatW,
        height: hatH,
        transformOrigin: `${BAND.cxFrac * 100}% ${BAND.cyFrac * 100}%`,
        transform:
          `translate(${px - BAND.cxFrac * hatW}px, ${py - BAND.cyFrac * hatH}px) rotate(${rot}deg)`,
        pointerEvents: 'none',
        zIndex: 4,
        filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.55))',
        userSelect: 'none',
        // Small transition smooths the ~15-20/s network cadence into fluid motion.
        transition: 'transform 60ms linear',
        willChange: 'transform',
      }}
      draggable={false}
    />
  );
};

export default JesterHat;
